/*
 * Copyright (c) 2023 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {Injectable} from '@angular/core';
import {Observable, throwError} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {MCMData} from '../models/mcm-data.model';
import {VehiclePortfolioLoad} from '../models/vehicle-portfolio-load.model';
import {VehicleSnapshotViolation} from '../models/vehicle-snapshot-violation.model';
import {VehicleSnapshot} from '../models/vehicle-snapshot.model';
import {ConfigService} from './config.service';

@Injectable({
  providedIn: 'root'
})

export class VehicleSnapshotService {
  constructor(private readonly configService: ConfigService,
              private readonly http: HttpClient) {
  }

  /**
   * Sends a get request to the specified API URL, which fetches the vehicle Snapshot
   * @return Observable<VehicleSnapshot[]>
   */
  getVehicleSnapshot(): Observable<VehicleSnapshot[]> {
    return this.http.get<VehicleSnapshot[]>(`${this.configService.baseApiUrl}portfolio/vehicle-snapshot`)
    .pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Transforms the GM3PD Vehicle snapshot the user uploads to an object to represent the data
   * @param file the GM3PD Vehicle snapshot file that is uploaded
   * @param sheetList a list containing the selected sheets to read data from
   */
  transform(file: File, sheetList: string[]): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheets', sheetList.toString());
    return this.http.post<any>(
      `${this.configService.baseApiUrl}portfolio/vehicle-snapshot/transform`, formData, {
        reportProgress: true,
        observe: 'events'
      }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Validate the vehicle snapshot and return a lists of violations if there are any violations
   * @param vehicleSnapshot - the vehicle snapshot data
   */
  validate(vehicleSnapshot: Map<string, VehicleSnapshot[]>): Observable<Map<string, VehicleSnapshotViolation>> {
    const url = `${this.configService.baseApiUrl}portfolio/vehicle-snapshot/validate`;
    return this.http.post<Map<string, VehicleSnapshotViolation>>(
      url, vehicleSnapshot).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Load the GM3PD Vehicle snapshot
   * @param vehicleSnapshot The GM3PD Vehicle snapshot
   * @return Observable<Map<string, VehicleSnapshot[]>> - returns the snapshot data that was loaded
   */
  load(vehicleSnapshot: Map<string, VehicleSnapshot[]>): Observable<Map<string, VehicleSnapshot[]>> {
    const url = `${this.configService.baseApiUrl}portfolio/vehicle-snapshot/load`;
    return this.http.post<Map<string, VehicleSnapshot[]>>(url, vehicleSnapshot).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Transforms the GM3PD MCM data snapshot the user uploads to an object to represent the data
   * @param file the GM3PD MCM data snapshot file that is uploaded
   * @param sheetList a list containing the selected sheets to read data from
   */
  transformMCMData(file: File, sheetList: string[]): Observable<any> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    formData.append('sheets', sheetList.toString());
    return this.http.post<any>(
      `${this.configService.baseApiUrl}portfolio/vehicle-snapshot/transform-mcm`, formData, {
        reportProgress: true,
        observe: 'events'
      }).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Loads MCM data from Excel worksheet into the system
   * @param mcmData - the MCM data to load
   */
  loadMCMData(mcmData: MCMData[]): Observable<void> {
    const url = `${this.configService.baseApiUrl}portfolio/vehicle-snapshot/load-mcm`;
    return this.http.post<void>(url, mcmData).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Process the Vehicle Snapshot in the system
   * @param gmid - the gmid of the user
   * @param recIdStart - the RecId to start at
   * @param portType - the Port Type (business plan or TVP)
   * @param keepRecId - boolean on whether to keep the RecID or not during the processing
   */
  processSnapshot(gmid: string, recIdStart: number, portType: string, keepRecId: boolean):
    Observable<Map<string, VehiclePortfolioLoad[]>> {
    const url = `${this.configService.baseApiUrl}portfolio/vehicle-snapshot/process`;
    const body = {
      gmid: gmid,
      recIdStart: recIdStart,
      portType: portType,
      keepRecId: keepRecId
    };
    return this.http.post<Map<string, VehiclePortfolioLoad[]>>(url, body).pipe(catchError(error => this.handleError(error)));
  }

  /**
   * Get the unmatched Vehicle Snapshot data, which contains Vehicle
   * @return Observable<VehicleSnapshot[]>
   */
  getUnmatchedVehicleSnapshot(): Observable<VehicleSnapshot[]> {
    return this.http.get<VehicleSnapshot[]>(`${this.configService.baseApiUrl}portfolio/vehicle-snapshot/unmatched-vehicle-snapshot`).pipe(
      map((res) => res, catchError(myError => this.handleError(myError))));
  }

  /**
   * Handles the errors produced by the backend
   * @param errorResponse The error produced by the API to be handled by the frontend
   * @return Observable
   */
  private handleError(errorResponse: HttpErrorResponse): Observable<any> {
    let errorMessage = 'An unexpected error has occurred.';
    if (errorResponse.error && errorResponse.error.message) {
      errorMessage = errorResponse.error.message;
    }
    return throwError(errorMessage);
  }

}
